import React from "react";
import { CheckCircle2, Info, TriangleAlert, XCircle } from "lucide-react";
import Modal from "./Modal";
import { useUiMessages } from "./UiMessages";

export type ConfirmDialogType = "warning" | "danger" | "info" | "success";

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  type?: ConfirmDialogType;
  confirmText?: string;
  cancelText?: string;
  closeLabel?: string;
  /** 在消息下方展示警示说明，适合危险/影响面大的操作 */
  riskMessage?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const CONFIGS: Record<ConfirmDialogType, { icon: React.ComponentType<{ className?: string }>; iconColor: string; btnColor: string }> = {
  danger:  { icon: XCircle,       iconColor: "text-danger",  btnColor: "bg-danger-strong hover:bg-danger-strong-hover" },
  success: { icon: CheckCircle2,  iconColor: "text-success", btnColor: "bg-action-primary hover:bg-action-primary-hover" },
  info:    { icon: Info,          iconColor: "text-info",    btnColor: "bg-action-primary hover:bg-action-primary-hover" },
  warning: { icon: TriangleAlert, iconColor: "text-warning", btnColor: "bg-action-primary hover:bg-action-primary-hover" },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen, title, message, type = "warning",
  confirmText, cancelText, closeLabel,
  riskMessage, onConfirm, onCancel,
}) => {
  const { dialog } = useUiMessages();
  const { icon: Icon, iconColor, btnColor } = CONFIGS[type];
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      contentClassName="p-6"
      showCloseButton
      closeLabel={closeLabel ?? dialog.closeConfirm}
      fallbackTitle={title}
    >
      <div className="flex items-start gap-4">
        <Icon className={`h-6 w-6 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1">
          <h2 className="text-lg font-medium text-text-primary">{title}</h2>
          <p className="mt-2 text-sm text-text-muted">{message}</p>
          {riskMessage && (
            <p className="mt-3 rounded-lg border border-warning-border bg-warning-soft px-3 py-2 text-xs leading-5 text-warning">
              {riskMessage}
            </p>
          )}
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-10 cursor-pointer rounded-lg border border-border-default bg-bg-surface px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-elevated"
        >
          {cancelText ?? dialog.cancel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={`min-h-10 cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-white ${btnColor}`}
        >
          {confirmText ?? dialog.confirm}
        </button>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
